import { apiFetch } from '@Shared/Api/apiClient';
import React, { useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Input, TextArea, FileUploadMultiple, Table as TableComponent } from '@Components';
import { APIS } from '@Config/apis';
import { DEFAULT_HEADER } from '@Config/userToken';
import userAuth from '@Config/userauth';
import { escapeHtml, formatMultilineTextAsHtml } from '@Shared/Utils/html.util';

const getAuthHeaders = () => {
  const headers = DEFAULT_HEADER.get?.() || {};
  const authorization = headers.Authorization || headers.authorization;

  return authorization ? { Authorization: authorization } : {};
};

const buildDetailHtml = ({
  cuit,
  fechaPago,
  numeroSolicitudPago,
  importePagado,
  asunto,
  detalle
}) => `
  <div style="font-family: Arial, Helvetica, sans-serif; color: #777777; background-color: #ffffff; padding: 28px; font-size: 16px; line-height: 1.35;">
    <div style="background-color: #e1fbfb; border-radius: 8px; padding: 10px 18px; margin-bottom: 16px;">
      <div style="font-size: 18px; color: #777777; margin-bottom: 4px;">CUIT Intermediario</div>
      <div style="font-size: 16px; color: #00aeb3;">${escapeHtml(cuit)}</div>
    </div>

    <div style="font-size: 18px; color: #777777; margin-bottom: 6px;">Consulta sobre</div>
    <table role="presentation" cellpadding="0" cellspacing="0" style="width: 100%; border-collapse: separate; border-spacing: 0; border: 1px solid #e1e5ea; border-radius: 8px; overflow: hidden; margin-bottom: 20px;">
      <thead>
        <tr>
          <th align="left" style="background-color: #f3f3f3; color: #777777; font-size: 16px; font-weight: 600; padding: 4px 10px;">Fecha de pago</th>
          <th align="left" style="background-color: #f3f3f3; color: #777777; font-size: 16px; font-weight: 600; padding: 4px 10px;">Solicitud de pago</th>
          <th align="left" style="background-color: #f3f3f3; color: #777777; font-size: 16px; font-weight: 600; padding: 4px 10px;">Importe</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="color: #777777; font-size: 16px; padding: 16px 10px; border-top: 1px solid #eef0f2;">${escapeHtml(fechaPago)}</td>
          <td style="color: #777777; font-size: 16px; padding: 16px 10px; border-top: 1px solid #eef0f2;">${escapeHtml(numeroSolicitudPago)}</td>
          <td style="color: #777777; font-size: 16px; padding: 16px 10px; border-top: 1px solid #eef0f2;">${escapeHtml(importePagado)}</td>
        </tr>
      </tbody>
    </table>

    <div style="background-color: #00aeb3; color: #ffffff; border-radius: 8px; padding: 14px 20px; margin-bottom: 14px; font-size: 20px; font-weight: 600;">
      ${escapeHtml(asunto)}
    </div>

    <div style="color: #777777; font-size: 18px;">
      ${formatMultilineTextAsHtml(detalle)}
    </div>
  </div>
`;

const ConsultaModal = ({ isOpen, onClose, onSubmitSuccess, item }) => {
  const [asunto, setAsunto] = useState('');
  const [detalle, setDetalle] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const fileUploadRef = useRef(null);

  const cuit = userAuth.isProducer() ? userAuth.cuit() : userAuth.cuitProducer();

  const summaryColumns = useMemo(() => ([
    {
      Header: 'Fecha de pago',
      accessor: 'fecha',
      width: 170,
      sortable: false,
      filterable: false
    },
    {
      Header: 'Solicitud de pago',
      accessor: 'numeroSolicitudPago',
      width: 140,
      sortable: false,
      filterable: false
    },
    {
      Header: 'Importe',
      accessor: 'importePagado',
      width: 160,
      sortable: false,
      filterable: false
    }
  ]), []);

  const summaryData = useMemo(() => ([{
    fecha: item?.fecha || '-',
    numeroSolicitudPago: item?.numeroSolicitudPago || item?.referencia || '-',
    importePagado: item?.importePagado || '-'
  }]), [item]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!asunto.trim() || !detalle.trim()) {
      setErrorMessage('Completa el asunto y el detalle para enviar la consulta.');
      return;
    }

    const fechaPago = item?.fecha || '-';
    const numeroSolicitudPago = item?.numeroSolicitudPago || item?.referencia || '-';
    const importePagado = item?.importePagado || '-';
    const detalleConContexto = [
      asunto.trim(),
      `Consulta sobre: Fecha de pago ${fechaPago}, Solicitud de pago ${numeroSolicitudPago}, Importe ${importePagado}.`,
      detalle.trim()
    ].join('\n\n');
    const detailHtml = buildDetailHtml({
      cuit,
      fechaPago,
      numeroSolicitudPago,
      importePagado,
      asunto: asunto.trim(),
      detalle: detalle.trim()
    });

    const formData = new FormData();
    formData.append('IdSector', '2');
    formData.append('IdSectorQueryReason', '');
    formData.append('Motive', `Nueva consulta de Portal | CUIT ${cuit} | Solicitud de pago ${numeroSolicitudPago}`);
    formData.append('Detail', detalleConContexto);
    formData.append('DetailHtml', detailHtml);
    formData.append('SendCopy', false);
    formData.append('NotificationType', 'NotificationConsultaSpecial');
    formData.append('User', userAuth.userId());

    const files = fileUploadRef.current?.getFiles?.() || [];

    for (const fileItem of files) {
      formData.append('FileName', fileItem.name);
      formData.append('File', fileItem.file, fileItem.name);
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const response = await apiFetch(APIS.URLS.CONSULTA_SEND, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'No se pudo enviar la consulta.');
      }

      onSubmitSuccess();
    } catch (error) {
      setErrorMessage(error?.message || 'No se pudo enviar la consulta.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      centered
      dialogClassName="consulta-payment-modal"
    >
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Consulta</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="mb-2">Consulta sobre</p>

          <div className="mb-3">
            <TableComponent
              columns={summaryColumns}
              data={summaryData}
              className="consulta-payment-modal__summary-table"
              filterable={false}
              hidePagination={true}
              hideFooter={true}
              isChildTable={true}
              defaultPageSize={1}
            />
          </div>

          <Input
            id="consulta-retenciones-asunto"
            label="Asunto"
            value={asunto}
            borderColor={errorMessage && !asunto.trim() ? 'error' : ''}
            onChange={(event) => setAsunto(event.target.value)}
          />

          <div className="input">
            <label htmlFor="consulta-retenciones-detalle">Contanos el detalle de tu consulta</label>
            <TextArea
              id="consulta-retenciones-detalle"
              text=""
              rowNum={5}
              defaultValue={detalle}
              onChange={(event) => setDetalle(event.target.value)}
            />
          </div>

          <div className="input consulta-payment-modal__file">
            <label>Adjuntar archivo (en caso de ser necesario)</label>
            <FileUploadMultiple ref={fileUploadRef} multiple={true} />
          </div>

          <div className="consulta-payment-modal__notice" role="alert">
            <i className="bi bi-info-circle" aria-hidden="true" />
            <span>
              Dentro de las 72 horas habiles responderemos a tu email registrado en el Portal.
              Si no lo encontras, revisa la carpeta de spam.
            </span>
          </div>

          {errorMessage && (
            <div className="alert alert-danger mt-3 mb-0" role="alert">
              {errorMessage}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="consulta-modal-footer border-0 d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Enviando' : 'Enviar'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default ConsultaModal;
